import { countries, CountryCode } from "@redotech/locale/countries";
import { useSearch } from "@redotech/react-util/search";
import { useTranslation } from "@redotech/redo-i18n";
import Fuse from "fuse.js";
import { memo, useMemo, useState } from "react";
import { BadgeRedoListItem } from "../list/redo-list";
import { RedoTagsDropdownInput } from "./redo-tags-dropdown-input";

const countryOptions: BadgeRedoListItem<{ code: CountryCode; name: string }>[] =
  countries.map((country) => ({
    id: country.code,
    value: { code: country.code, name: country.name },
    type: "badge" as const,
    badge: { text: country.name },
  }));

const countrySearcher = new Fuse<
  BadgeRedoListItem<{ code: CountryCode; name: string }>
>(countryOptions, { keys: ["value.name", "value.code"] });

export const CountriesPicker = memo(function CountriesPicker({
  countryCodes,
  setCountryCodes,
  disabled,
}: {
  countryCodes: string[];
  setCountryCodes: (codes: string[]) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const [searchString, setSearchString] = useState("");

  const filteredOptions = useSearch(
    countrySearcher,
    countryOptions,
    searchString,
  );

  const selectedOptions: BadgeRedoListItem<{
    code: CountryCode;
    name: string;
  }>[] = useMemo(() => {
    return countryOptions.filter((option) =>
      countryCodes.includes(option.value.code),
    );
  }, [countryCodes]);

  return (
    <RedoTagsDropdownInput
      disabled={disabled}
      options={filteredOptions}
      placeholder={t("All countries (leave empty for all)")}
      searchPlaceholder={t("Search countries", { context: "country picker" })}
      searchString={searchString}
      selectedOptions={selectedOptions}
      setSearchString={setSearchString}
      setSelectedOptions={(items) => {
        setCountryCodes(items.map((item) => item.value.code));
      }}
      size="xs"
    />
  );
});
